import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useDietitianFiltering } from '../../hooks/useDietitianFiltering';
import { TitleText, BodyText, Button, Card, Input, Select } from '../ui';
import { DietitianList } from './DietitianList';
import { cn } from '../../utils/cn';
import { dieticianDomain } from '../../services/domains/DieticianDomain';
import type { DietitianProfile, FilterState } from '../../types/referral';
import { HEALTH_CONDITIONS } from '../../types/referral';
import { getLogger } from '@/services/shared/LoggerFactory';

interface DietitianSelectionStepProps {
  dietitians: DietitianProfile[];
  selectedDietitian: DietitianProfile | null;
  filters: FilterState;
  onDietitianSelect: (dietitian: DietitianProfile) => void;
  onFiltersChange: (filters: Partial<FilterState>) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
}

/**
 * Enhanced Dietitian Selection Step with complete filtering and selection functionality
 * Phase 3 implementation with real dietitian data, search, and filtering capabilities
 */
export const DietitianSelectionStep: React.FC<DietitianSelectionStepProps> = ({
  selectedDietitian,
  filters,
  onDietitianSelect,
  onFiltersChange,
  onNext,
  onBack,
  isLoading: externalLoading = false,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [localDietitians, setLocalDietitians] = useState<DietitianProfile[]>([]);
  const [isLoadingDietitians, setIsLoadingDietitians] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local filter state for immediate UI updates
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Use the filtering hook
  const {
    filteredDietitians,
    applyFilters,
    clearFilters,
    isFiltered,
  } = useDietitianFiltering(localDietitians, localFilters);

  // Calculate pagination
  const totalPages = Math.ceil(filteredDietitians.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDietitians = filteredDietitians.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [localFilters]);

  // Load dietitians on component mount
  useEffect(() => {
    const loadDietitians = async () => {
      setIsLoadingDietitians(true);
      setError(null);
      try {
        // Try to load from API, fallback to mock data
        const apiDietitians = await dieticianDomain.searchDietitians();
        setLocalDietitians(apiDietitians);
      } catch (err) {
        logger.warn('Failed to load dietitians from API, using mock data:', err);
        setLocalDietitians([]);
      } finally {
        setIsLoadingDietitians(false);
      }
    };

    loadDietitians();
  }, []);

  // Update parent filters when local filters change
  useEffect(() => {
    onFiltersChange(localFilters);
    applyFilters(localFilters);
  }, [localFilters, onFiltersChange, applyFilters]);

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setLocalFilters(prev => ({ ...prev, search: value }));
  }, []);

  // Handle specialty filter change
  const handleSpecialtyChange = useCallback((value: string) => {
    setLocalFilters(prev => ({ ...prev, specialty: value }));
  }, []);

  // Handle location filter change
  const handleLocationChange = useCallback((value: string) => {
    setLocalFilters(prev => ({ ...prev, location: value }));
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const clearedFilters = { search: '', specialty: '', location: '' };
    setLocalFilters(clearedFilters);
    clearFilters();
  }, [clearFilters]);

  // Handle next step
  const handleNext = () => {
    if (!selectedDietitian) {
      setError('Please select a dietitian to continue');
      return;
    }
    setError(null);
    onNext();
  };

  // Get filter options - use HEALTH_CONDITIONS for specialty
  const specialtyOptions = HEALTH_CONDITIONS;

  const isLoading = externalLoading || isLoadingDietitians;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <TitleText level={2} className="mb-2">
          Select Dietitian
        </TitleText>
        <BodyText variant="secondary">
          Choose an appropriate dietitian for your patient based on their health conditions and location.
        </BodyText>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-2">
          <Card className={cn(
            'p-6',
            isDark ? 'bg-secondary-800' : 'bg-white'
          )}>
            <TitleText level={4} className="mb-4">
              Filter Dietitians
            </TitleText>

            <div className="space-y-4">
              {/* Search Input */}
              <div>
                <BodyText variant="secondary" size="sm" className="mb-2">
                  Search by name or specialty
                </BodyText>
                <Input
                  type="text"
                  placeholder="Search dietitians..."
                  value={localFilters.search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Specialty Filter */}
              <div>
                <BodyText variant="secondary" size="sm" className="mb-2">
                  Specialty
                </BodyText>
                <Select
                  value={localFilters.specialty}
                  onChange={(e) => handleSpecialtyChange(e.target.value)}
                  className="w-full"
                  options={specialtyOptions.map(option => (
                    {value: option, label: option, disabled: false}
                  ))}
                  required
                >
                </Select>
              </div>

              {/* Location Filter */}
              <div>
                <BodyText variant="secondary" size="sm" className="mb-2">
                  Location
                </BodyText>
                <Input
                  type="text"
                  placeholder="Filter by location..."
                  value={localFilters.location}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Clear Filters Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleClearFilters}
                disabled={!isFiltered}
              >
                Clear Filters
              </Button>
            </div>
          </Card>
        </div>

        {/* Dietitian List */}
        <div className="lg:col-span-2">
          <DietitianList
            dietitians={paginatedDietitians}
            selectedDietitian={selectedDietitian}
            onDietitianSelect={onDietitianSelect}
            onClearFilters={handleClearFilters}
            isLoading={isLoading}
            isFiltered={isFiltered}
            // Pagination props
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredDietitians.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className={cn(
          'p-4 border-l-4 border-error-500',
          isDark ? 'bg-error-900/20' : 'bg-error-50'
        )}>
          <BodyText variant="primary" className={cn("font-medium", isDark ? 'text-error-400' : 'text-error-600')}>
            {error}
          </BodyText>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isLoading}
        >
          Back: Patient Details
        </Button>

        <Button
          onClick={handleNext}
          disabled={isLoading || !selectedDietitian}
        >
          {isLoading ? 'Loading...' : 'Next: Review & Submit'}
        </Button>
      </div>
    </div>
  );
};